/**
 * EditServiceAccountModal Component
 *
 * Modal for editing an existing service account.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useUpdateServiceAccount } from '../hooks/useUpdateServiceAccount';
import type { ServiceAccountSanitized, ServiceAccountStatus } from '../../../types/service-account.types';

// Status values
const STATUS_OPTIONS: { value: ServiceAccountStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

// Validation schema
const editServiceAccountSchema = z.object({
  serviceAccountName: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  status: z.enum(['active', 'inactive', 'suspended']),
});

type EditServiceAccountFormData = z.infer<typeof editServiceAccountSchema>;

interface EditServiceAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  serviceAccount: ServiceAccountSanitized | null;
}

export const EditServiceAccountModal: React.FC<EditServiceAccountModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  serviceAccount,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<EditServiceAccountFormData>({
    resolver: zodResolver(editServiceAccountSchema),
  });

  // Reset form when service account changes
  useEffect(() => {
    if (serviceAccount) {
      reset({
        serviceAccountName: serviceAccount.serviceAccountName,
        description: serviceAccount.description || '',
        status: serviceAccount.status,
      });
    }
  }, [serviceAccount, reset]);

  const { mutate: updateServiceAccount, isPending } = useUpdateServiceAccount(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Service account updated successfully!');
      setErrorMessage('');
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: EditServiceAccountFormData) => {
    if (!serviceAccount || !isDirty) return;

    setErrorMessage('');
    setSuccessMessage('');

    updateServiceAccount({
      serviceAccountId: serviceAccount.serviceAccountId,
      data: {
        serviceAccountName: data.serviceAccountName,
        description: data.description || undefined,
        status: data.status,
      },
    });
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  if (!serviceAccount) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Edit Service Account</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Code (read-only) */}
          <FormField label="Code" helperText="Code is immutable and cannot be changed">
            <Input
              value={serviceAccount.serviceAccountCode}
              disabled
              className="bg-gray-50"
            />
          </FormField>

          {/* Name */}
          <FormField
            label="Name"
            required
            error={errors.serviceAccountName?.message}
          >
            <Input
              {...register('serviceAccountName')}
              error={!!errors.serviceAccountName}
            />
          </FormField>

          {/* Status */}
          <FormField
            label="Status"
            required
            error={errors.status?.message}
          >
            <Select {...register('status')} error={!!errors.status}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>

          {/* Description */}
          <FormField
            label="Description"
            error={errors.description?.message}
          >
            <Textarea
              {...register('description')}
              rows={2}
              error={!!errors.description}
            />
          </FormField>

          {/* Note about roles */}
          <Alert variant="info">
            To assign roles to this service account, use the Roles page and add this service account to the desired role.
          </Alert>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending} disabled={!isDirty}>
              Save Changes
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
